import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TabBar from '../components/TabBar';
import AgentRunList from '../components/agent/AgentRunList';
import AgentRunDetail from '../components/agent/AgentRunDetail';
import InboundEmailList from '../components/agent/InboundEmailList';
import ApprovalList from '../components/agent/ApprovalList';
import ArtifactGrid from '../components/agent/ArtifactGrid';
import AgentSettingsPanel from '../components/agent/AgentSettingsPanel';
import ManualRunModal from '../components/agent/ManualRunModal';
import { agentApi } from '../services/agentApi';
import { btn } from '../components/agent/agentUi';

type TabKey = 'runs' | 'inbox' | 'approvals' | 'artifacts' | 'settings';
const TABS: TabKey[] = ['runs', 'inbox', 'approvals', 'artifacts', 'settings'];

/**
 * Agent Activity (AGENT_MAILBOX_PLAN.md §8.1): everything the autonomous
 * Arie did — inbound mail, runs with a step-by-step trace, artifacts,
 * approvals, and the agency's agent settings. Deep links: ?run=<id>,
 * ?tab=<tab>, ?artifact=<id>.
 */
const AgentActivity: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const runId = params.get('run');
  const tab = (TABS.includes(params.get('tab') as TabKey) ? params.get('tab') : 'runs') as TabKey;
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshCounts = useCallback(() => {
    agentApi.notifications().then((n) => setPendingApprovals(n.pendingApprovals)).catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshCounts();
    const t = setInterval(refreshCounts, 30000);
    return () => clearInterval(t);
  }, [refreshCounts]);

  const go = (next: { tab?: TabKey; run?: string | null; artifact?: string | null }) => {
    const p = new URLSearchParams();
    const nextTab = next.tab ?? tab;
    if (nextTab !== 'runs') p.set('tab', nextTab);
    if (next.run) p.set('run', next.run);
    if (next.artifact) p.set('artifact', next.artifact);
    setParams(p);
  };

  const openRun = (id: string, artifactId?: string) => go({ tab: 'runs', run: id, artifact: artifactId || null });

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 text-gray-900 dark:text-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agent</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Email Arie your work — every step it takes is traced here.</p>
        </div>
        <button className={btn.primary} onClick={() => setShowManual(true)}>Run Arie…</button>
      </div>

      <TabBar
        items={[
          { key: 'runs', label: 'Runs' },
          { key: 'inbox', label: 'Inbox' },
          { key: 'approvals', label: 'Approvals', badge: pendingApprovals > 0 ? pendingApprovals : undefined },
          { key: 'artifacts', label: 'Artifacts' },
          { key: 'settings', label: 'Settings' },
        ]}
        activeKey={tab}
        onChange={(k) => go({ tab: k as TabKey, run: null })}
      />

      {tab === 'runs' && (runId ? (
        <AgentRunDetail runId={runId} onBack={() => go({ tab: 'runs', run: null })} highlightArtifactId={params.get('artifact')} />
      ) : (
        <AgentRunList onOpen={(id) => openRun(id)} refreshKey={refreshKey} />
      ))}
      {tab === 'inbox' && <InboundEmailList onOpenRun={(id) => openRun(id)} refreshKey={refreshKey} />}
      {tab === 'approvals' && <ApprovalList onOpenRun={(id) => openRun(id)} onChanged={refreshCounts} />}
      {tab === 'artifacts' && <ArtifactGrid onOpenRun={openRun} />}
      {tab === 'settings' && <AgentSettingsPanel onOpenRun={(id) => openRun(id)} />}

      {showManual && (
        <ManualRunModal
          onClose={() => setShowManual(false)}
          onStarted={(id) => {
            setShowManual(false);
            setRefreshKey((k) => k + 1);
            openRun(id);
          }}
        />
      )}
    </div>
  );
};

export default AgentActivity;
